import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  StatusBadge,
  cn,
} from "../../../shared/ui";
import type {
  LeaderboardItem,
  LeaderboardReport,
  LeaderboardRow,
} from "../../../shared/types/api";
import { Figure } from "./reportBits";
import { FilterBar, defaultFilters, toQuery, type ReportFilterValues } from "./reportFilters";
import { DEFAULT_WEIGHTS, composite, rating, type Weights } from "./leaderboardScoring";
import { awaitingAfterDevelopment } from "../../workitems/workItemVocabulary";

/**
 * Top-scored employees: a transparent score out of a hundred and a star rating, with the four
 * components the score is made of and every task behind it — including the ones they skipped.
 *
 * <p>The weights are the manager's to set: change them and every score, star and rank recomputes
 * live from the components the API returned, so a team can agree what "good" means here rather
 * than inherit it. The formula and each component stay on screen, so a ranking is defensible line
 * by line rather than a black box handing out stars.
 */
export function Leaderboard() {
  const [filters, setFilters] = useState<ReportFilterValues>(defaultFilters);

  const report = useQuery({
    queryKey: ["report", "leaderboard", filters],
    queryFn: () => api.get<LeaderboardReport>(`/api/v1/reports/leaderboard?${toQuery(filters)}`),
  });

  return (
    <div className="space-y-4">
      <FilterBar values={filters} onChange={setFilters} />

      {report.isPending ? (
        <LoadingState label="Scoring the team" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <Report data={report.data} />
      )}
    </div>
  );
}

type SortKey =
  | "score"
  | "onTime"
  | "estimate"
  | "reliability"
  | "productivity"
  | "completed"
  | "skipped"
  | "logged";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "score", label: "Score" },
  { key: "onTime", label: "On-time" },
  { key: "estimate", label: "Estimate accuracy" },
  { key: "reliability", label: "Reliability" },
  { key: "productivity", label: "Productivity" },
  { key: "completed", label: "Completed" },
  { key: "skipped", label: "Skipped" },
  { key: "logged", label: "Hours logged" },
];

/** A row with its score and rating recomputed under the currently chosen weights. */
interface ScoredRow extends LeaderboardRow {
  liveScore: number;
  liveRating: number;
}

function Report({ data }: { data: LeaderboardReport }) {
  const [weights, setWeights] = useState<Weights>(DEFAULT_WEIGHTS);
  const [sort, setSort] = useState<SortKey>("score");

  const scored = useMemo<ScoredRow[]>(() => {
    const rows = data.people.map((row) => {
      const liveScore = composite(
        {
          onTime: row.onTimeScore,
          estimate: row.estimateScore,
          reliability: row.reliabilityScore,
          productivity: row.productivityScore,
        },
        weights,
      );
      return { ...row, liveScore, liveRating: rating(liveScore) };
    });
    rows.sort((a, b) => sortValue(b, sort) - sortValue(a, sort));
    return rows;
  }, [data.people, weights, sort]);

  const averageScore =
    scored.length === 0 ? 0 : scored.reduce((sum, row) => sum + row.liveScore, 0) / scored.length;
  const totalCompleted = scored.reduce((sum, row) => sum + row.completed, 0);
  const totalSkipped = scored.reduce((sum, row) => sum + row.skippedCount, 0);
  const judged = scored.reduce((sum, row) => sum + row.completedOnTime + row.completedLate, 0);
  const onTimeAll = scored.reduce((sum, row) => sum + row.completedOnTime, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure
          label="Team average"
          value={String(Math.round(averageScore))}
          note={`${"★".repeat(rating(averageScore))}${"☆".repeat(5 - rating(averageScore))}`}
        />
        <Figure label="Delivered" value={String(totalCompleted)} note="completed in range" tone="green" />
        <Figure
          label="On-time across team"
          value={judged === 0 ? "—" : `${Math.round((onTimeAll / judged) * 100)}%`}
          note="of finished work with a deadline"
        />
        <Figure
          label="Skipped"
          value={String(totalSkipped)}
          note="cancelled work"
          tone={totalSkipped > 0 ? "amber" : undefined}
        />
      </div>

      <Card>
        <CardHeader
          title="Team leaderboard"
          subtitle="Set what counts, and the ranking follows."
          action={
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-500">Sort by</span>
              <Select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                {SORTS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          }
        />
        <CardBody className="space-y-3">
          <WeightControls weights={weights} onChange={setWeights} />
          {scored.length === 0 ? (
            <EmptyState title="Nobody to score" message="No assigned work in this window and filter." />
          ) : (
            <ul className="-mx-4 divide-y divide-ink-100 border-t border-ink-100">
              {scored.map((row, index) => (
                <PersonRow key={row.employeeId} row={row} rank={index + 1} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function sortValue(row: ScoredRow, key: SortKey): number {
  switch (key) {
    case "score":
      return row.liveScore;
    case "onTime":
      return row.onTimeScore ?? -1;
    case "estimate":
      return row.estimateScore ?? -1;
    case "reliability":
      return row.reliabilityScore ?? -1;
    case "productivity":
      return row.productivityScore ?? -1;
    case "completed":
      return row.completed;
    case "skipped":
      return row.skippedCount;
    case "logged":
      return row.loggedHours;
    default:
      return row.liveScore;
  }
}

/** The four weights, adjustable live. They need not sum to a hundred — the score renormalises. */
function WeightControls({
  weights,
  onChange,
}: {
  weights: Weights;
  onChange: (next: Weights) => void;
}) {
  const set = (key: keyof Weights) => (event: { target: { value: string } }) => {
    const value = Math.max(0, Math.min(100, Number(event.target.value) || 0));
    onChange({ ...weights, [key]: value });
  };
  const fields: { key: keyof Weights; label: string }[] = [
    { key: "onTime", label: "On-time" },
    { key: "estimate", label: "Estimate" },
    { key: "reliability", label: "Reliability" },
    { key: "productivity", label: "Productivity" },
  ];
  const isDefault =
    weights.onTime === DEFAULT_WEIGHTS.onTime &&
    weights.estimate === DEFAULT_WEIGHTS.estimate &&
    weights.reliability === DEFAULT_WEIGHTS.reliability &&
    weights.productivity === DEFAULT_WEIGHTS.productivity;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg bg-ink-50 px-3 py-2.5">
      <span className="text-xs font-medium text-ink-600">Weights</span>
      {fields.map((field) => (
        <label key={field.key} className="flex flex-col text-[11px] text-ink-500">
          {field.label}
          <Input
            type="number"
            min={0}
            max={100}
            value={String(weights[field.key])}
            onChange={set(field.key)}
            className="mt-0.5 w-16"
          />
        </label>
      ))}
      {!isDefault && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_WEIGHTS)}
          className="text-xs text-brand-700 hover:text-brand-800"
        >
          Reset
        </button>
      )}
    </div>
  );
}

const MEDAL: Record<number, string> = {
  1: "bg-amber-100 text-amber-800 ring-1 ring-amber-300",
  2: "bg-ink-100 text-ink-700 ring-1 ring-ink-300",
  3: "bg-orange-100 text-orange-800 ring-1 ring-orange-300",
};

function PersonRow({ row, rank }: { row: ScoredRow; rank: number }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span
          className={cn(
            "tnum flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
            MEDAL[rank] ?? "text-ink-500",
          )}
        >
          {rank}
        </span>

        <div className="min-w-[10rem] flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink-900">{row.employeeName}</span>
            {row.department && <span className="text-xs text-ink-500">{row.department}</span>}
          </div>
          <Stars rating={row.liveRating} />
        </div>

        <div className="text-right">
          <div className="tnum font-display text-2xl font-semibold text-ink-900">
            {Math.round(row.liveScore)}
          </div>
          <div className="text-xs text-ink-500">score</div>
        </div>

        <div className="hidden w-64 shrink-0 space-y-1 md:block">
          <Component label="On-time" value={row.onTimeScore} />
          <Component label="Estimate" value={row.estimateScore} />
          <Component label="Reliability" value={row.reliabilityScore} />
          <Component label="Productivity" value={row.productivityScore} />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-12 text-xs">
        <Chip tone="green">{row.completedOnTime} on time</Chip>
        {row.completedLate > 0 && <Chip tone="amber">{row.completedLate} late</Chip>}
        {row.overdueOpenCount > 0 && <Chip tone="red">{row.overdueOpenCount} overdue</Chip>}
        {row.skippedCount > 0 && <Chip tone="red">{row.skippedCount} skipped</Chip>}
        {row.openCount > 0 && <Chip tone="gray">{row.openCount} open</Chip>}
        <span className="tnum text-ink-500">{Math.round(row.loggedHours * 10) / 10}h logged</span>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="ml-auto text-brand-700 hover:text-brand-800"
        >
          {open
            ? "Hide tasks"
            : `Show ${row.items.length} ${row.items.length === 1 ? "task" : "tasks"}`}
        </button>
      </div>

      {open && (
        <ul className="mt-2 space-y-1 pl-12">
          {row.items.map((item) => (
            <ItemLine key={item.workItemId} item={item} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Stars({ rating: value }: { rating: number }) {
  return (
    <div className="text-sm leading-none text-amber-500" aria-label={`${value} out of 5`}>
      <span>{"★".repeat(value)}</span>
      <span className="text-ink-300">{"★".repeat(5 - value)}</span>
    </div>
  );
}

function Component({ label, value }: { label: string; value: number | null }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-[11px] text-ink-500">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
        <div
          className={cn(
            "h-full rounded-full",
            value == null
              ? "bg-ink-200"
              : pct >= 80
                ? "bg-emerald-500"
                : pct >= 50
                  ? "bg-amber-500"
                  : "bg-red-500",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tnum w-8 shrink-0 text-right text-[11px] text-ink-500">
        {value == null ? "—" : Math.round(value)}
      </span>
    </div>
  );
}

const OUTCOME: Record<
  LeaderboardItem["outcome"],
  { label: string; tone: "green" | "amber" | "red" | "gray" | "blue" }
> = {
  ON_TIME: { label: "On time", tone: "green" },
  DONE: { label: "Done", tone: "green" },
  LATE: { label: "Late", tone: "amber" },
  OVERDUE: { label: "Overdue", tone: "red" },
  OPEN: { label: "Open", tone: "gray" },
  SKIPPED: { label: "Skipped", tone: "red" },
};

function ItemLine({ item }: { item: LeaderboardItem }) {
  const outcome = OUTCOME[item.outcome] ?? { label: item.outcome, tone: "gray" as const };
  return (
    <li className="flex flex-wrap items-baseline gap-2 text-xs">
      <StatusBadge tone={outcome.tone}>{outcome.label}</StatusBadge>
      <Link
        to={`/work-items/${item.itemKey}`}
        className="font-mono text-ink-500 hover:text-brand-600"
      >
        {item.itemKey}
      </Link>
      <span className="text-ink-700">{item.title}</span>
      {item.daysLate != null && item.daysLate > 0 && (
        <span className="text-amber-700">{item.daysLate}d late</span>
      )}
      {/* Late at development, and still on its way through review or testing. */}
      {awaitingAfterDevelopment(item.status) && (
        <span className="text-ink-500">{awaitingAfterDevelopment(item.status)}</span>
      )}
      {item.overrunPercent != null && item.overrunPercent > 0 && (
        <span className="text-red-700">+{Math.round(item.overrunPercent)}% over</span>
      )}
    </li>
  );
}

function Chip({
  tone,
  children,
}: {
  tone: "green" | "amber" | "red" | "gray";
  children: ReactNode;
}) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    gray: "bg-ink-100 text-ink-600",
  };
  return <span className={cn("rounded-full px-2 py-0.5", tones[tone])}>{children}</span>;
}
